import { ref, reactive, computed } from 'vue'
import { useChatSocket } from './useChatSocket'
import type { ThreadRow } from './useChatThreads'

/**
 * Inbox badge state: per-thread unread counts + running total.
 *
 * Authoritative source: `GET /api/chat/inbox/summary` at load time +
 * on `__open` reconnect. Incremental updates arrive via `thread.unread`
 * WS frames on the user channel.
 *
 * The "threads" list here is specifically "threads I have unread in,"
 * used to render the Inbox tab. Full DM list lives in `useChatThreads`.
 */

const totalUnread = ref(0)
const unreadByThread = reactive(new Map<string, number>())
const unreadThreadRows = ref<ThreadRow[]>([])
const loading = ref(false)
const loaded = ref(false)

let wired = false

function recomputeTotal() {
    let n = 0
    for (const v of unreadByThread.values()) n += v
    totalUnread.value = n
}

async function loadSummary() {
    loading.value = true
    try {
        const res = await $fetch<{ totalUnread: number; threads: ThreadRow[] }>('/api/chat/inbox/summary')
        unreadByThread.clear()
        for (const t of res.threads || []) {
            if (t.unread > 0) unreadByThread.set(t.id, t.unread)
        }
        unreadThreadRows.value = res.threads || []
        totalUnread.value = res.totalUnread
        loaded.value = true
    } catch (err: any) {
        if (err?.statusCode === 404) loaded.value = true
        else console.error('[chat:inbox] load failed', err)
    } finally {
        loading.value = false
    }
}

function wire() {
    if (wired) return
    wired = true
    const { on } = useChatSocket()

    on('thread.unread', (frame) => {
        if (typeof frame?.threadId !== 'string') return
        const count = Math.max(0, Number(frame.count) || 0)
        if (count === 0) {
            unreadByThread.delete(frame.threadId)
            unreadThreadRows.value = unreadThreadRows.value.filter(t => t.id !== frame.threadId)
        } else {
            unreadByThread.set(frame.threadId, count)
            const existing = unreadThreadRows.value.find(t => t.id === frame.threadId)
            if (existing) {
                existing.unread = count
            } else {
                // New unread thread — refresh summary to get hydrated row
                loadSummary()
                return
            }
        }
        recomputeTotal()
    })

    on('__open', () => { if (loaded.value) loadSummary() })
}

export function useChatInbox() {
    if (import.meta.client) {
        wire()
        if (!loaded.value) loadSummary()
    }

    return {
        totalUnread: computed(() => totalUnread.value),
        unreadByThread: computed(() => unreadByThread),
        unreadThreads: computed(() => unreadThreadRows.value),
        loading: computed(() => loading.value),
        reload: loadSummary,
    }
}
